export const webDevelopmentCourse = {
  id: 'web-development',
  title: 'Web Development',
  description: 'Comprehensive course covering modern web development technologies including HTML, CSS, JavaScript, React, Node.js, and full-stack application development.',
  status: 'published',
  duration: '15 weeks',
  level: 'Intermediate',
  credits: 3,
  prerequisites: ['Programming Fundamentals', 'Data Structures and Algorithms'],
  learningObjectives: [
    'Master HTML5, CSS3, and responsive web design principles',
    'Develop interactive web applications using JavaScript and React',
    'Build server-side applications with Node.js and Express',
    'Implement database integration and API development',
    'Apply modern web development tools and deployment strategies',
    'Understand web security and performance optimization'
  ],
  modules: [
    {
      id: 'frontend-fundamentals',
      title: 'Frontend Fundamentals',
      description: 'Master HTML5, CSS3, JavaScript, and responsive design principles for modern web development.',
      duration: '4 weeks',
      lessons: [
        {
          id: 'html-css-basics',
          title: 'HTML5 and CSS3 Fundamentals',
          content: `
# HTML5 and CSS3 Fundamentals

## Learning Objectives
- Master semantic HTML5 elements and structure
- Apply CSS3 styling, layouts, and responsive design
- Understand accessibility and SEO best practices
- Implement modern CSS features and animations

## HTML5 Semantic Elements

### Document Structure
\`\`\`html
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="Page description for SEO">
    <title>Page Title</title>
</head>
<body>
    <header>
        <nav>
            <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#contact">Contact</a></li>
            </ul>
        </nav>
    </header>
    
    <main>
        <section id="hero">
            <h1>Main Heading</h1>
            <p>Hero section content</p>
        </section>
        
        <section id="features">
            <h2>Features</h2>
            <article>
                <h3>Feature 1</h3>
                <p>Feature description</p>
            </article>
        </section>
    </main>
    
    <aside>
        <h3>Sidebar Content</h3>
        <p>Additional information</p>
    </aside>
    
    <footer>
        <p>&copy; 2024 Your Website. All rights reserved.</p>
    </footer>
</body>
</html>
\`\`\`

### Form Elements
\`\`\`html
<form action="/submit" method="POST" novalidate>
    <fieldset>
        <legend>Personal Information</legend>
        
        <label for="name">Full Name:</label>
        <input type="text" id="name" name="name" required>
        
        <label for="email">Email:</label>
        <input type="email" id="email" name="email" required>
        
        <label for="phone">Phone:</label>
        <input type="tel" id="phone" name="phone">
        
        <label for="birthday">Birthday:</label>
        <input type="date" id="birthday" name="birthday">
        
        <label for="message">Message:</label>
        <textarea id="message" name="message" rows="4" cols="50"></textarea>
        
        <label for="country">Country:</label>
        <select id="country" name="country">
            <option value="">Select Country</option>
            <option value="us">United States</option>
            <option value="ca">Canada</option>
            <option value="uk">United Kingdom</option>
        </select>
        
        <fieldset>
            <legend>Preferred Contact Method</legend>
            <input type="radio" id="contact-email" name="contact" value="email">
            <label for="contact-email">Email</label>
            
            <input type="radio" id="contact-phone" name="contact" value="phone">
            <label for="contact-phone">Phone</label>
        </fieldset>
        
        <input type="checkbox" id="newsletter" name="newsletter">
        <label for="newsletter">Subscribe to newsletter</label>
        
        <button type="submit">Submit</button>
        <button type="reset">Reset</button>
    </fieldset>
</form>
\`\`\`

## CSS3 Fundamentals

### CSS Grid Layout
\`\`\`css
.container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-gap: 20px;
    padding: 20px;
}

.grid-item {
    background: #f4f4f4;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Named Grid Lines */
.layout {
    display: grid;
    grid-template-columns: [sidebar-start] 250px [sidebar-end main-start] 1fr [main-end];
    grid-template-rows: [header-start] 60px [header-end content-start] 1fr [content-end footer-start] 40px [footer-end];
    grid-template-areas: 
        "header header"
        "sidebar main"
        "footer footer";
    min-height: 100vh;
}

.header { grid-area: header; }
.sidebar { grid-area: sidebar; }
.main { grid-area: main; }
.footer { grid-area: footer; }
\`\`\`

### Flexbox Layout
\`\`\`css
.flex-container {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;
}

.flex-item {
    flex: 1 1 300px; /* grow, shrink, basis */
    min-width: 0; /* Allow shrinking below content size */
}

/* Responsive Navigation */
.nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
}

.nav-menu {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 2rem;
}

@media (max-width: 768px) {
    .nav-menu {
        display: none;
        flex-direction: column;
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        background: white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .nav-menu.active {
        display: flex;
    }
}
\`\`\`

### CSS Custom Properties (Variables)
\`\`\`css
:root {
    --primary-color: #3498db;
    --secondary-color: #2ecc71;
    --text-color: #333;
    --bg-color: #f8f9fa;
    --font-size-base: 16px;
    --font-size-large: 1.25rem;
    --spacing-unit: 8px;
    --border-radius: 4px;
    --box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.button {
    background: var(--primary-color);
    color: white;
    padding: calc(var(--spacing-unit) * 2) calc(var(--spacing-unit) * 3);
    border: none;
    border-radius: var(--border-radius);
    font-size: var(--font-size-base);
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.button:hover {
    background: color-mix(in srgb, var(--primary-color) 80%, black);
}

.button--secondary {
    background: var(--secondary-color);
}
\`\`\`

### Responsive Design
\`\`\`css
/* Mobile-first approach */
.container {
    padding: 1rem;
    max-width: 100%;
}

/* Tablet */
@media (min-width: 768px) {
    .container {
        padding: 2rem;
        max-width: 750px;
        margin: 0 auto;
    }
}

/* Desktop */
@media (min-width: 1024px) {
    .container {
        max-width: 1200px;
        padding: 3rem;
    }
}

/* High DPI displays */
@media (-webkit-min-device-pixel-ratio: 2),
       (min-resolution: 192dpi) {
    .logo {
        background-image: url('logo@2x.png');
        background-size: 100px 50px;
    }
}

/* Print styles */
@media print {
    .no-print {
        display: none;
    }
    
    body {
        font-size: 12pt;
        line-height: 1.4;
    }
    
    a::after {
        content: " (" attr(href) ")";
    }
}
\`\`\`

### CSS Animations and Transitions
\`\`\`css
/* Transitions */
.card {
    transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.card:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.2);
}

/* Keyframe Animations */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-in {
    animation: fadeInUp 0.6s ease-out;
}

/* Loading Spinner */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid var(--primary-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

/* Pulse Animation */
@keyframes pulse {
    0%, 100% {
        opacity: 1;
        transform: scale(1);
    }
    50% {
        opacity: 0.7;
        transform: scale(1.05);
    }
}

.pulse {
    animation: pulse 2s ease-in-out infinite;
}
\`\`\`

## Accessibility Best Practices

### ARIA Attributes
\`\`\`html
<!-- Skip Navigation -->
<a href="#main-content" class="skip-link">Skip to main content</a>

<!-- Accessible Form -->
<form role="form" aria-labelledby="form-title">
    <h2 id="form-title">Contact Information</h2>
    
    <div class="form-group">
        <label for="email">Email Address</label>
        <input type="email" 
               id="email" 
               name="email" 
               required 
               aria-describedby="email-help"
               aria-invalid="false">
        <div id="email-help" class="help-text">
            We'll never share your email with anyone else.
        </div>
    </div>
    
    <!-- Error State -->
    <div class="form-group error">
        <label for="password">Password</label>
        <input type="password" 
               id="password" 
               name="password" 
               required 
               aria-describedby="password-error"
               aria-invalid="true">
        <div id="password-error" class="error-message" role="alert">
            Password must be at least 8 characters long.
        </div>
    </div>
</form>

<!-- Accessible Navigation -->
<nav role="navigation" aria-label="Main navigation">
    <ul>
        <li><a href="/" aria-current="page">Home</a></li>
        <li><a href="/about">About</a></li>
        <li><a href="/contact">Contact</a></li>
    </ul>
</nav>

<!-- Modal Dialog -->
<div class="modal" role="dialog" aria-labelledby="modal-title" aria-modal="true">
    <div class="modal-content">
        <h2 id="modal-title">Confirm Action</h2>
        <p>Are you sure you want to delete this item?</p>
        <button type="button" class="btn-primary">Confirm</button>
        <button type="button" class="btn-secondary" aria-label="Cancel and close dialog">
            Cancel
        </button>
    </div>
</div>
\`\`\`

## Modern CSS Features

### Container Queries
\`\`\`css
.card-container {
    container-type: inline-size;
    container-name: card;
}

@container card (min-width: 400px) {
    .card {
        display: flex;
        flex-direction: row;
    }
    
    .card-image {
        flex: 0 0 200px;
    }
    
    .card-content {
        flex: 1;
        padding-left: 1rem;
    }
}
\`\`\`

### Logical Properties
\`\`\`css
.text-content {
    /* Instead of margin-left and margin-right */
    margin-inline: 1rem;
    
    /* Instead of margin-top and margin-bottom */
    margin-block: 2rem;
    
    /* Instead of padding-left */
    padding-inline-start: 1rem;
    
    /* Instead of border-left */
    border-inline-start: 2px solid var(--primary-color);
}
\`\`\`

## Applications
- Modern web applications and websites
- Progressive web apps (PWAs)
- Email templates and newsletters
- Digital marketing materials
- Interactive prototypes and designs
          `,
          interactiveExercises: [
            {
              id: 'responsive-layout',
              title: 'Responsive Web Layout Builder',
              description: 'Build responsive layouts using CSS Grid and Flexbox',
              initialCode: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Responsive Layout</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        /* Add your CSS here */
        .container {
            /* Implement responsive container */
        }
        
        .header {
            /* Style the header */
        }
        
        .nav {
            /* Create responsive navigation */
        }
        
        .main-content {
            /* Main content area with CSS Grid */
        }
        
        .sidebar {
            /* Sidebar styling */
        }
        
        .footer {
            /* Footer styling */
        }
        
        .card {
            /* Card component */
        }
        
        /* Add responsive breakpoints */
        @media (max-width: 768px) {
            /* Mobile styles */
        }
        
        @media (min-width: 769px) and (max-width: 1024px) {
            /* Tablet styles */
        }
        
        @media (min-width: 1025px) {
            /* Desktop styles */
        }
    </style>
</head>
<body>
    <div class="container">
        <header class="header">
            <h1>My Website</h1>
            <nav class="nav">
                <ul>
                    <li><a href="#home">Home</a></li>
                    <li><a href="#about">About</a></li>
                    <li><a href="#services">Services</a></li>
                    <li><a href="#contact">Contact</a></li>
                </ul>
            </nav>
        </header>
        
        <main class="main-content">
            <section class="content">
                <h2>Main Content</h2>
                <div class="cards">
                    <div class="card">
                        <h3>Card 1</h3>
                        <p>This is the first card content.</p>
                    </div>
                    <div class="card">
                        <h3>Card 2</h3>
                        <p>This is the second card content.</p>
                    </div>
                    <div class="card">
                        <h3>Card 3</h3>
                        <p>This is the third card content.</p>
                    </div>
                </div>
            </section>
            
            <aside class="sidebar">
                <h3>Sidebar</h3>
                <p>Additional information and links.</p>
            </aside>
        </main>
        
        <footer class="footer">
            <p>&copy; 2024 My Website. All rights reserved.</p>
        </footer>
    </div>
</body>
</html>`,
              solution: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Responsive Layout</title>
    <style>
        :root {
            --primary-color: #3498db;
            --secondary-color: #2ecc71;
            --text-color: #333;
            --bg-color: #f8f9fa;
            --border-color: #dee2e6;
            --spacing: 1rem;
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: var(--text-color);
            background-color: var(--bg-color);
        }
        
        .container {
            display: grid;
            grid-template-areas: 
                "header"
                "main"
                "footer";
            grid-template-rows: auto 1fr auto;
            min-height: 100vh;
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 var(--spacing);
        }
        
        .header {
            grid-area: header;
            background: white;
            padding: var(--spacing);
            border-bottom: 2px solid var(--border-color);
            margin-bottom: var(--spacing);
        }
        
        .header h1 {
            color: var(--primary-color);
            margin-bottom: var(--spacing);
        }
        
        .nav ul {
            display: flex;
            list-style: none;
            gap: 2rem;
            flex-wrap: wrap;
        }
        
        .nav a {
            text-decoration: none;
            color: var(--text-color);
            font-weight: 500;
            padding: 0.5rem 1rem;
            border-radius: 4px;
            transition: background-color 0.3s ease;
        }
        
        .nav a:hover {
            background-color: var(--primary-color);
            color: white;
        }
        
        .main-content {
            grid-area: main;
            display: grid;
            grid-template-columns: 1fr;
            gap: var(--spacing);
            margin-bottom: var(--spacing);
        }
        
        .content {
            background: white;
            padding: 2rem;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .content h2 {
            color: var(--primary-color);
            margin-bottom: var(--spacing);
        }
        
        .cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: var(--spacing);
            margin-top: 2rem;
        }
        
        .card {
            background: var(--bg-color);
            padding: var(--spacing);
            border-radius: 8px;
            border: 1px solid var(--border-color);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        
        .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
        }
        
        .card h3 {
            color: var(--secondary-color);
            margin-bottom: 0.5rem;
        }
        
        .sidebar {
            background: white;
            padding: var(--spacing);
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            height: fit-content;
        }
        
        .sidebar h3 {
            color: var(--secondary-color);
            margin-bottom: var(--spacing);
        }
        
        .footer {
            grid-area: footer;
            background: var(--text-color);
            color: white;
            text-align: center;
            padding: var(--spacing);
            margin-top: var(--spacing);
        }
        
        /* Mobile styles */
        @media (max-width: 768px) {
            .container {
                padding: 0 0.5rem;
            }
            
            .nav ul {
                flex-direction: column;
                gap: 0.5rem;
            }
            
            .cards {
                grid-template-columns: 1fr;
            }
            
            .content, .sidebar {
                padding: var(--spacing);
            }
        }
        
        /* Tablet styles */
        @media (min-width: 769px) and (max-width: 1024px) {
            .main-content {
                grid-template-columns: 1fr 250px;
            }
            
            .cards {
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            }
        }
        
        /* Desktop styles */
        @media (min-width: 1025px) {
            .main-content {
                grid-template-columns: 1fr 300px;
            }
            
            .cards {
                grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            }
            
            .header {
                display: flex;
                justify-content: space-between;
                align-items: center;
            }
            
            .header h1 {
                margin-bottom: 0;
            }
        }
        
        /* High DPI displays */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .card {
                border-width: 0.5px;
            }
        }
        
        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
            :root {
                --text-color: #f8f9fa;
                --bg-color: #212529;
                --border-color: #495057;
            }
            
            body {
                background-color: #121212;
            }
            
            .header, .content, .sidebar {
                background: #1a1a1a;
                color: var(--text-color);
            }
            
            .card {
                background: #2a2a2a;
                border-color: var(--border-color);
            }
        }
        
        /* Reduced motion for accessibility */
        @media (prefers-reduced-motion: reduce) {
            *,
            *::before,
            *::after {
                animation-duration: 0.01ms !important;
                animation-iteration-count: 1 !important;
                transition-duration: 0.01ms !important;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <header class="header">
            <h1>My Website</h1>
            <nav class="nav">
                <ul>
                    <li><a href="#home">Home</a></li>
                    <li><a href="#about">About</a></li>
                    <li><a href="#services">Services</a></li>
                    <li><a href="#contact">Contact</a></li>
                </ul>
            </nav>
        </header>
        
        <main class="main-content">
            <section class="content">
                <h2>Main Content</h2>
                <p>Welcome to our responsive website. This layout adapts to different screen sizes using CSS Grid and Flexbox.</p>
                <div class="cards">
                    <div class="card">
                        <h3>Responsive Design</h3>
                        <p>Our layout works perfectly on mobile, tablet, and desktop devices.</p>
                    </div>
                    <div class="card">
                        <h3>Modern CSS</h3>
                        <p>Built with CSS Grid, Flexbox, and modern CSS features for optimal performance.</p>
                    </div>
                    <div class="card">
                        <h3>Accessibility</h3>
                        <p>Designed with accessibility in mind, supporting screen readers and keyboard navigation.</p>
                    </div>
                </div>
            </section>
            
            <aside class="sidebar">
                <h3>Quick Links</h3>
                <p>Find additional resources and information in our sidebar section.</p>
                <ul style="margin-top: 1rem; padding-left: 1rem;">
                    <li>Documentation</li>
                    <li>Support</li>
                    <li>Community</li>
                </ul>
            </aside>
        </main>
        
        <footer class="footer">
            <p>&copy; 2024 My Website. All rights reserved.</p>
        </footer>
    </div>
</body>
</html>`,
              hints: [
                'Use CSS Grid for overall layout structure',
                'Apply Flexbox for navigation and card layouts',
                'Implement mobile-first responsive design',
                'Use CSS custom properties for consistent theming',
                'Add hover effects and transitions for interactivity'
              ]
            }
          ]
        }
      ]
    }
  ],
  assessments: [],
  resources: [],
  courseFormat: 'hybrid',
  gradingScale: {
    'A': { min: 90, max: 100 },
    'B': { min: 80, max: 89 },
    'C': { min: 70, max: 79 },
    'D': { min: 60, max: 69 },
    'F': { min: 0, max: 59 }
  },
  policies: {
    attendance: 'Regular attendance required for hands-on coding sessions. More than 2 unexcused absences may affect project grades.',
    lateWork: 'Late assignments penalized 10% per day. Web projects have specific deployment deadlines.',
    academicIntegrity: 'Individual coding required. Code sharing and collaboration allowed for learning but not for assessment submissions.',
    makeup: 'Makeup opportunities for missed practical sessions. Alternative projects may be assigned.',
    technology: 'Personal laptop required with modern web browser. Course provides cloud development environment access.',
    communication: 'Discord server for real-time help. Email response within 24 hours during weekdays.',
    accommodations: 'Students requiring accommodations should coordinate with Disability Services early in the semester.'
  }
};